import { graphql } from 'react-relay';
import type { Dispatch, SetStateAction } from 'react';
import { useState, useEffect } from 'react';

import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers.ts';
import { getSpaceLink } from '@confluence/route-manager-utils/entry-points/url.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { PAGE_SIZE } from './RecommendationTile.tsx';
import type { useFeedRecommendationsFeedRecommendationQuery as useFeedRecommendationsFeedRecommendationQuery$data } from './__types__/useFeedRecommendationsFeedRecommendationQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
const useFeedRecommendationsFeedRecommendationQuery = graphql`
	query useFeedRecommendationsFeedRecommendationQuery {
		getRecommendedFeedUserConfig {
			recommendedSpaces {
				score
				space {
					id
					key
					name
					icon {
						path
					}
				}
			}
			recommendedPeople {
				score
				user {
					id
					title
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
			}
		}
		userPreferences {
			feedRecommendedUserSettingsDismissTimestamp
		}
	}
`;
export type RecommendationType = {
	score: number;
	name: string;
	icon: string;
	id: string;
	url: string;
	type: 'space' | 'user';
	title?: string;
};

export const useFeedRecommendations = (): {
	showFeedRecs: boolean;
	setShowFeedRecs: Dispatch<SetStateAction<boolean>>;
	recs: RecommendationType[];
	setRecs: Dispatch<SetStateAction<RecommendationType[]>>;
	recsLoading: boolean;
	feedRecError: ApolloErrorInterop | undefined;
} => {
	const {
		data,
		loading: recsLoading,
		error: feedRecError,
	} = useQueryInterop<useFeedRecommendationsFeedRecommendationQuery$data>(
		useFeedRecommendationsFeedRecommendationQuery,
		{
			errorPolicy: 'all',
			// Use cache-first to avoid a redundant network roundtrip on every mount
			// when recommendations data is already cached. Mutations that dismiss
			// recommendations update the cache directly, so freshness is maintained.
			fetchPolicy: 'cache-first',
		},
	);

	const [recs, setRecs] = useState<RecommendationType[]>([]);
	const [initialRecSize, setInitialRec] = useState<number>(0);
	const recsDismissed = data?.userPreferences?.feedRecommendedUserSettingsDismissTimestamp;
	const userRecs = data?.getRecommendedFeedUserConfig?.recommendedPeople;
	const spaceRecs = data?.getRecommendedFeedUserConfig?.recommendedSpaces;

	const msInMonth = 2629800000;
	const showRecs =
		recsDismissed && recsDismissed !== 'NO_VALUE_SET'
			? Math.floor((new Date().getTime() - new Date(recsDismissed).getTime()) / msInMonth) >= 1
			: recsDismissed === 'NO_VALUE_SET';

	const initialState = showRecs && initialRecSize >= PAGE_SIZE && recs.length > 0;

	const [showFeedRecs, setShowFeedRecs] = useState(initialState);

	if (initialState != showFeedRecs) {
		setShowFeedRecs(initialState);
	}

	useEffect(() => {
		const userRecsArray: RecommendationType[] = [];
		const spaceRecsArray: RecommendationType[] = [];

		if (userRecs) {
			userRecs.forEach((user) => {
				if (user.user && user.user.displayName && user.user.id && user.user.isActive) {
					userRecsArray.push({
						score: user.score,
						name: user.user.displayName,
						icon: user.user.photos?.find((photo) => photo?.isPrimary)?.value || '',
						id: user.user.id,
						url: getAtlassianPeopleProfileUrl(user.user.id, 'feed-alpha', CONTEXT_PATH),
						title: user.user.title || '',
						type: 'user',
					});
				}
			});
		}

		if (spaceRecs) {
			spaceRecs.forEach((space) => {
				if (space.space && space.space.name && space.space.id && space.space.key)
					spaceRecsArray.push({
						score: space.score,
						name: space.space.name,
						icon: space.space.icon?.path || '',
						id: space.space.id,
						url: getSpaceLink(space.space.key),
						type: 'space',
					});
			});
		}

		userRecsArray.sort((a, b) => b.score - a.score);
		spaceRecsArray.sort((a, b) => b.score - a.score);
		const recsArray = userRecsArray.concat(spaceRecsArray);
		setRecs(recsArray);
		setInitialRec(recsArray.length);
	}, [userRecs, spaceRecs, setRecs, setInitialRec]);

	return {
		showFeedRecs,
		setShowFeedRecs,
		recs,
		setRecs,
		recsLoading,
		feedRecError,
	};
};
